// src/services/cofounder/tools/actionTools.js
import { registerTool } from './ToolRegistry';
import { db } from '../../../firebase/config';
import { collection, addDoc, doc, updateDoc, getDoc, serverTimestamp, query, where, getDocs, limit } from 'firebase/firestore';

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const getScopedBusinessId = (args, context) =>
  args.businessId || context.activeBusiness?.id || context.userProfile?.businessId || '';

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveInteger = (value, fallback = 7) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.round(parsed);
};

const getNextRunDate = (days) => {
  const cadenceDays = toPositiveInteger(days, 7);
  const nextRun = new Date();
  nextRun.setDate(nextRun.getDate() + cadenceDays);
  return nextRun;
};

async function fetchUserTransactionsByBusiness(businessId) {
  if (!businessId) return [];

  const txQuery = query(
    collection(db, 'financialTransactions'),
    where('businessId', '==', businessId),
    limit(250)
  );

  const txSnap = await getDocs(txQuery);
  return txSnap.docs.map((txDoc) => ({ id: txDoc.id, ...txDoc.data() }));
}

export function registerActionTools() {
  registerTool({
    name: 'createMarketingCampaign',
    description: 'Create a deliberate marketing campaign draft tied to the active business.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Campaign name' },
        objective: { type: 'string', description: 'Business objective (e.g., lead generation, retention, awareness)' },
        channel: { type: 'string', enum: ['email', 'social', 'sms', 'ads', 'network'], description: 'Primary channel' },
        audience: { type: 'string', description: 'Audience segment or persona' },
        budget: { type: 'number', description: 'Optional campaign budget' },
        startDate: { type: 'string', description: 'Optional start date in YYYY-MM-DD format' },
        endDate: { type: 'string', description: 'Optional end date in YYYY-MM-DD format' },
        notes: { type: 'string', description: 'Optional planning notes' },
        businessId: { type: 'string', description: 'Optional business ID override' }
      },
      required: ['name']
    },
    execute: async (args, context) => {
      const name = (args.name || '').trim();
      if (!name) return { error: 'Campaign name is required' };

      const businessId = getScopedBusinessId(args, context);
      const userId = context.currentUser?.uid;
      if (!userId) return { error: 'No authenticated user found' };

      const campaign = {
        recordType: 'campaign',
        deliberateBy: 'cofounder',
        businessId: businessId || null,
        createdBy: userId,
        name,
        objective: args.objective || 'lead generation',
        channel: args.channel || 'email',
        audience: args.audience || '',
        budget: Math.max(0, toNumberOrNull(args.budget) || 0),
        notes: args.notes || '',
        status: 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (args.startDate) campaign.startDate = new Date(args.startDate);
      if (args.endDate) campaign.endDate = new Date(args.endDate);

      const campaignRef = await addDoc(collection(db, 'marketing'), campaign);
      return {
        success: true,
        campaignId: campaignRef.id,
        name: campaign.name,
        channel: campaign.channel,
        objective: campaign.objective
      };
    }
  });

  registerTool({
    name: 'createMarketingAutomation',
    description: 'Create a deliberate auto-marketing workflow with cadence and next run schedule.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Automation name' },
        trigger: { type: 'string', enum: ['weekly', 'new_lead', 'low_engagement', 'event_based'], description: 'Automation trigger type' },
        cadenceDays: { type: 'number', description: 'Run cadence in days' },
        objective: { type: 'string', description: 'Automation objective' },
        channel: { type: 'string', enum: ['email', 'social', 'sms', 'ads', 'network'], description: 'Primary channel' },
        audience: { type: 'string', description: 'Audience segment' },
        enabled: { type: 'boolean', description: 'Whether the automation should be active' },
        nextRun: { type: 'string', description: 'Optional next run date in YYYY-MM-DD format' },
        businessId: { type: 'string', description: 'Optional business ID override' }
      },
      required: ['name']
    },
    execute: async (args, context) => {
      const name = (args.name || '').trim();
      if (!name) return { error: 'Automation name is required' };

      const businessId = getScopedBusinessId(args, context);
      const userId = context.currentUser?.uid;
      if (!userId) return { error: 'No authenticated user found' };

      const cadenceDays = toPositiveInteger(args.cadenceDays, 7);
      const automation = {
        recordType: 'automation',
        deliberateBy: 'cofounder',
        businessId: businessId || null,
        createdBy: userId,
        name,
        trigger: args.trigger || 'weekly',
        cadenceDays,
        objective: args.objective || '',
        channel: args.channel || 'email',
        audience: args.audience || '',
        enabled: args.enabled !== false,
        nextRun: args.nextRun ? new Date(args.nextRun) : getNextRunDate(cadenceDays),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const automationRef = await addDoc(collection(db, 'marketing'), automation);
      return {
        success: true,
        automationId: automationRef.id,
        name: automation.name,
        trigger: automation.trigger,
        cadenceDays: automation.cadenceDays
      };
    }
  });

  registerTool({
    name: 'runMarketingAutomation',
    description: 'Mark a marketing automation as deliberately executed and schedule its next run.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        automationId: { type: 'string', description: 'Automation document ID from marketing collection' },
        cadenceDays: { type: 'number', description: 'Optional cadence override for next run in days' },
        executionNote: { type: 'string', description: 'Optional note about what was executed' }
      },
      required: ['automationId']
    },
    execute: async (args, context) => {
      const automationRef = doc(db, 'marketing', args.automationId);
      const automationDoc = await getDoc(automationRef);
      if (!automationDoc.exists()) return { error: 'Marketing automation not found' };

      const automation = automationDoc.data();
      if ((automation.recordType || '') !== 'automation') {
        return { error: 'Record is not a marketing automation' };
      }

      const cadenceDays = toPositiveInteger(args.cadenceDays, automation.cadenceDays || 7);
      const updatePayload = {
        cadenceDays,
        lastRun: serverTimestamp(),
        nextRun: getNextRunDate(cadenceDays),
        updatedAt: serverTimestamp(),
        updatedBy: context.currentUser?.uid || null
      };

      if (args.executionNote) {
        updatePayload.lastExecutionNote = args.executionNote;
      }

      await updateDoc(automationRef, updatePayload);
      return {
        success: true,
        automationId: args.automationId,
        name: automation.name || '',
        cadenceDays,
        message: 'Automation marked as run and next run rescheduled.'
      };
    }
  });

  registerTool({
    name: 'createFinancialTransaction',
    description: 'Create a new financial transaction record (income or expense). Useful for recording payments and subscriptions.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        description: { type: 'string', description: 'Transaction description' },
        amount: { type: 'number', description: 'Transaction amount as a positive number' },
        direction: { type: 'string', enum: ['income', 'expense'], description: 'Money direction. Use expense for outgoing payments.' },
        date: { type: 'string', description: 'Transaction date in YYYY-MM-DD format' },
        category: { type: 'string', description: 'Category such as utilities, rent, sales, other_income, other_expense' },
        subcategory: { type: 'string', description: 'Optional subcategory' },
        notes: { type: 'string', description: 'Optional notes' },
        reference: { type: 'string', description: 'Reference number or short label' },
        paymentMethod: {
          type: 'string',
          enum: ['bank_transfer', 'cash', 'credit_card', 'debit_card', 'mobile_money', 'other'],
          description: 'Payment method'
        },
        accountId: { type: 'string', description: 'Account document ID. If omitted, coFounder will try to use the first account in the active business.' },
        businessId: { type: 'string', description: 'Business document ID. Optional if an active business context exists.' }
      },
      required: ['description', 'amount']
    },
    execute: async (args, context) => {
      const amount = toNumberOrNull(args.amount);
      if (amount === null || amount <= 0) {
        return { error: 'Amount must be a positive number' };
      }

      const businessId = getScopedBusinessId(args, context);
      if (!businessId) {
        return { error: 'No business context found. Provide businessId or select an active business.' };
      }

      let accountId = args.accountId || '';
      if (!accountId) {
        const accountsQuery = query(
          collection(db, 'accounts'),
          where('businessId', '==', businessId),
          limit(1)
        );
        const accountsSnap = await getDocs(accountsQuery);
        if (!accountsSnap.empty) {
          accountId = accountsSnap.docs[0].id;
        }
      }

      if (!accountId) {
        return { error: 'No account found for this business. Please create an account first or provide accountId.' };
      }

      const direction = args.direction === 'income' ? 'income' : 'expense';
      const signedAmount = direction === 'expense' ? -Math.abs(amount) : Math.abs(amount);
      const userId = context.currentUser?.uid;

      const transaction = {
        businessId,
        accountId,
        type: 'simple',
        date: args.date ? new Date(args.date) : new Date(),
        amount: signedAmount,
        description: args.description,
        notes: args.notes || '',
        category: args.category || (direction === 'expense' ? 'other_expense' : 'other_income'),
        subcategory: args.subcategory || '',
        reference: args.reference || '',
        paymentMethod: args.paymentMethod || 'bank_transfer',
        status: 'completed',
        source: 'cofounder',
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      const txRef = await addDoc(collection(db, 'financialTransactions'), transaction);
      return {
        success: true,
        transactionId: txRef.id,
        type: transaction.type,
        direction,
        amount: Math.abs(signedAmount),
        description: transaction.description
      };
    }
  });

  registerTool({
    name: 'createTask',
    description: 'Create a new task in the task management system.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Task title' },
        description: { type: 'string', description: 'Task description' },
        priority: { type: 'string', enum: ['low', 'medium', 'high', 'urgent'], description: 'Task priority' },
        dueDate: { type: 'string', description: 'Due date in YYYY-MM-DD format' }
      },
      required: ['title']
    },
    execute: async (args, context) => {
      const task = {
        title: args.title,
        description: args.description || '',
        priority: args.priority || 'medium',
        status: 'pending',
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      if (args.dueDate) {
        task.dueDate = new Date(args.dueDate);
      }
      const docRef = await addDoc(collection(db, 'tasks'), task);
      return { success: true, taskId: docRef.id, title: args.title };
    }
  });

  registerTool({
    name: 'updateInventoryItem',
    description: 'Adjust stock level for an inventory item with a reason.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        itemId: { type: 'string', description: 'Stock item ID' },
        location: { type: 'string', description: 'Location/warehouse name' },
        adjustment: { type: 'number', description: 'Stock adjustment (positive to add, negative to subtract)' },
        reason: { type: 'string', description: 'Reason for the adjustment' }
      },
      required: ['itemId', 'adjustment', 'reason']
    },
    execute: async (args, context) => {
      const itemRef = doc(db, 'stockItems', args.itemId);
      const location = args.location || 'default';

      const { getDoc } = await import('firebase/firestore');
      const itemDoc = await getDoc(itemRef);
      if (!itemDoc.exists()) return { error: 'Item not found' };

      const itemData = itemDoc.data();
      const locations = itemData.locations || {};
      const currentQty = locations[location] || 0;
      locations[location] = Math.max(0, currentQty + args.adjustment);

      await updateDoc(itemRef, { locations, updatedAt: serverTimestamp() });

      await addDoc(collection(db, 'stockAdjustments'), {
        itemId: args.itemId,
        location,
        previousQty: currentQty,
        newQty: locations[location],
        adjustment: args.adjustment,
        reason: args.reason,
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp()
      });

      return { success: true, itemId: args.itemId, newQuantity: locations[location] };
    }
  });

  registerTool({
    name: 'draftEmail',
    description: 'Prepare an email draft for review. Does NOT send the email.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        to: { type: 'string', description: 'Recipient email address' },
        subject: { type: 'string', description: 'Email subject line' },
        body: { type: 'string', description: 'Email body content' }
      },
      required: ['to', 'subject', 'body']
    },
    execute: async (args, context) => {
      const draft = {
        to: args.to,
        subject: args.subject,
        body: args.body,
        status: 'draft',
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'emailDrafts'), draft);
      return { success: true, draftId: docRef.id, message: 'Email draft created. Review and send from Communication > Email.' };
    }
  });

  registerTool({
    name: 'createLead',
    description: 'Add a new lead to the CRM system.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Lead name (person or company)' },
        email: { type: 'string', description: 'Contact email' },
        phone: { type: 'string', description: 'Contact phone' },
        source: { type: 'string', description: 'Lead source (e.g., referral, website, network)' },
        notes: { type: 'string', description: 'Additional notes' }
      },
      required: ['name']
    },
    execute: async (args, context) => {
      const lead = {
        name: args.name,
        email: args.email || '',
        phone: args.phone || '',
        source: args.source || 'coFounder AI',
        notes: args.notes || '',
        status: 'new',
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'leads'), lead);
      return { success: true, leadId: docRef.id, name: args.name };
    }
  });

  registerTool({
    name: 'scheduleMeeting',
    description: 'Create a new meeting entry in the calendar.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Meeting title' },
        date: { type: 'string', description: 'Meeting date in YYYY-MM-DD format' },
        time: { type: 'string', description: 'Meeting time in HH:MM format' },
        duration: { type: 'number', description: 'Duration in minutes' },
        description: { type: 'string', description: 'Meeting description/agenda' }
      },
      required: ['title', 'date', 'time']
    },
    execute: async (args, context) => {
      const meeting = {
        title: args.title,
        date: args.date,
        time: args.time,
        duration: args.duration || 60,
        description: args.description || '',
        status: 'scheduled',
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'meetings'), meeting);
      return { success: true, meetingId: docRef.id, title: args.title };
    }
  });

  registerTool({
    name: 'createPurchaseOrder',
    description: 'Initiate a purchase order for inventory restocking.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        supplierId: { type: 'string', description: 'Supplier ID' },
        items: {
          type: 'string',
          description: 'JSON string of items array: [{itemId, name, quantity, unitPrice}]'
        },
        notes: { type: 'string', description: 'Order notes' }
      },
      required: ['items']
    },
    execute: async (args, context) => {
      let items;
      try {
        items = typeof args.items === 'string' ? JSON.parse(args.items) : args.items;
      } catch (e) {
        return { error: 'Invalid items format' };
      }

      const totalAmount = items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0), 0);

      const po = {
        supplierId: args.supplierId || '',
        items,
        totalAmount,
        notes: args.notes || '',
        status: 'draft',
        businessId: context.activeBusiness?.id || context.userProfile?.businessId,
        createdBy: context.currentUser?.uid,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'purchaseOrders'), po);
      return { success: true, purchaseOrderId: docRef.id, totalAmount };
    }
  });

  registerTool({
    name: 'postToNetwork',
    description: 'Draft a network post for review before publishing.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        content: { type: 'string', description: 'Post content' },
        tags: { type: 'string', description: 'Comma-separated tags' }
      },
      required: ['content']
    },
    execute: async (args, context) => {
      const tags = args.tags ? args.tags.split(',').map(t => t.trim()) : [];
      const post = {
        content: args.content,
        tags,
        authorId: context.userProfile?.businessId,
        status: 'draft',
        likeCount: 0,
        commentCount: 0,
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'posts'), post);
      return { success: true, postId: docRef.id, message: 'Network post drafted. Review it in your Network feed.' };
    }
  });

  // --- Update Tools ---

  registerTool({
    name: 'updateTaskStatus',
    description: 'Update the status of an existing task (e.g., mark as completed, cancelled, in progress).',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        taskId: { type: 'string', description: 'The task document ID' },
        status: { type: 'string', enum: ['pending', 'in_progress', 'completed', 'on_hold', 'cancelled'], description: 'New status' }
      },
      required: ['taskId', 'status']
    },
    execute: async (args, context) => {
      const taskRef = doc(db, 'tasks', args.taskId);
      const taskDoc = await getDoc(taskRef);
      if (!taskDoc.exists()) return { error: 'Task not found' };

      await updateDoc(taskRef, {
        status: args.status,
        updatedAt: serverTimestamp(),
        updatedBy: context.currentUser?.uid
      });
      return { success: true, taskId: args.taskId, newStatus: args.status, title: taskDoc.data().title };
    }
  });

  registerTool({
    name: 'updateInvoiceStatus',
    description: 'Update the status of an invoice or bill (e.g., mark as paid, sent, overdue). If transactionId is missing, coFounder can find the transaction document ID by documentNumber/reference/description/amount.',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        transactionId: { type: 'string', description: 'The financial transaction document ID (preferred when known)' },
        documentNumber: { type: 'string', description: 'Invoice or bill number to match (e.g., INV-2503-101)' },
        reference: { type: 'string', description: 'Reference field to match' },
        description: { type: 'string', description: 'Transaction description to match' },
        amount: { type: 'number', description: 'Invoice/bill amount to help matching' },
        paidAmount: { type: 'number', description: 'Optional paid amount to store when status is paid' },
        businessId: { type: 'string', description: 'Optional business ID to narrow the search' },
        status: { type: 'string', enum: ['pending', 'sent', 'paid', 'overdue', 'cancelled', 'void'], description: 'New status' }
      },
      required: ['status']
    },
    execute: async (args, context) => {
      const businessId = getScopedBusinessId(args, context);
      let matchedTransaction = null;
      let matchedBy = '';

      if (args.transactionId) {
        const txRef = doc(db, 'financialTransactions', args.transactionId);
        const txDoc = await getDoc(txRef);
        if (!txDoc.exists()) return { error: 'Transaction not found' };

        const txData = txDoc.data();
        if (txData.type !== 'invoice' && txData.type !== 'bill') {
          return { error: 'Transaction is not an invoice or bill' };
        }

        matchedTransaction = { id: txDoc.id, ...txData };
        matchedBy = 'transactionId';
      } else {
        const hasLookupField = args.documentNumber || args.reference || args.description || args.amount !== undefined;
        if (!hasLookupField) {
          return {
            error: 'Provide transactionId or one of documentNumber/reference/description/amount so I can find the transaction.'
          };
        }

        if (!businessId) {
          return {
            error: 'No active business context found. Provide businessId or transactionId for secure lookup.'
          };
        }

        const allTransactions = await fetchUserTransactionsByBusiness(businessId);
        const invoiceBillTransactions = allTransactions.filter((tx) => tx.type === 'invoice' || tx.type === 'bill');

        const normalizedDocumentNumber = normalizeText(args.documentNumber);
        const normalizedReference = normalizeText(args.reference);
        const normalizedDescription = normalizeText(args.description);
        const amount = toNumberOrNull(args.amount);

        const matches = invoiceBillTransactions.filter((tx) => {
          if (normalizedDocumentNumber && normalizeText(tx.documentNumber) !== normalizedDocumentNumber) return false;
          if (normalizedReference && normalizeText(tx.reference) !== normalizedReference) return false;

          if (normalizedDescription) {
            const txDescription = normalizeText(tx.description);
            if (!txDescription.includes(normalizedDescription) && !normalizedDescription.includes(txDescription)) {
              return false;
            }
          }

          if (amount !== null) {
            const txAmount = toNumberOrNull(tx.totalAmount ?? tx.amount);
            if (txAmount === null || Math.abs(Math.abs(txAmount) - Math.abs(amount)) > 0.01) return false;
          }

          return true;
        });

        if (matches.length === 1) {
          matchedTransaction = matches[0];
          matchedBy = normalizedDocumentNumber ? 'documentNumber' : normalizedReference ? 'reference' : normalizedDescription ? 'description' : 'amount';
        } else if (matches.length > 1) {
          return {
            error: 'Multiple transactions matched. Please provide transactionId.',
            matches: matches.slice(0, 5).map((tx) => ({
              transactionId: tx.id,
              documentNumber: tx.documentNumber || '',
              description: tx.description || '',
              amount: tx.totalAmount ?? tx.amount ?? 0,
              status: tx.status || ''
            }))
          };
        }
      }

      if (!matchedTransaction) return { error: 'Transaction not found' };

      const updateData = {
        status: args.status,
        updatedAt: serverTimestamp(),
        updatedBy: context.currentUser?.uid
      };

      if (args.status === 'paid') {
        const defaultPaidAmount = toNumberOrNull(matchedTransaction.totalAmount ?? matchedTransaction.amount);
        const paidAmount = toNumberOrNull(args.paidAmount) ?? defaultPaidAmount ?? 0;
        updateData.paidAt = serverTimestamp();
        updateData.paidAmount = paidAmount;
        if (defaultPaidAmount !== null) {
          updateData.balanceDue = Math.max(0, defaultPaidAmount - paidAmount);
        } else {
          updateData.balanceDue = 0;
        }
      }

      const txRef = doc(db, 'financialTransactions', matchedTransaction.id);
      await updateDoc(txRef, updateData);
      return {
        success: true,
        transactionId: matchedTransaction.id,
        documentNumber: matchedTransaction.documentNumber || '',
        matchedBy,
        newStatus: args.status
      };
    }
  });

  registerTool({
    name: 'updateLeadStatus',
    description: 'Update the status of a CRM lead (e.g., mark as contacted, qualified, converted, lost).',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        leadId: { type: 'string', description: 'The lead document ID' },
        status: { type: 'string', enum: ['new', 'contacted', 'qualified', 'negotiation', 'converted', 'lost'], description: 'New status' }
      },
      required: ['leadId', 'status']
    },
    execute: async (args, context) => {
      const leadRef = doc(db, 'leads', args.leadId);
      const leadDoc = await getDoc(leadRef);
      if (!leadDoc.exists()) return { error: 'Lead not found' };

      await updateDoc(leadRef, {
        status: args.status,
        updatedAt: serverTimestamp(),
        updatedBy: context.currentUser?.uid
      });
      return { success: true, leadId: args.leadId, newStatus: args.status, name: leadDoc.data().name };
    }
  });

  registerTool({
    name: 'updateOrderStatus',
    description: 'Update the status of a purchase order (e.g., mark as approved, received, cancelled).',
    category: 'write',
    requiresConfirmation: true,
    parameters: {
      type: 'object',
      properties: {
        orderId: { type: 'string', description: 'The purchase order document ID' },
        status: { type: 'string', enum: ['draft', 'pending', 'approved', 'received', 'cancelled'], description: 'New status' }
      },
      required: ['orderId', 'status']
    },
    execute: async (args, context) => {
      const orderRef = doc(db, 'purchaseOrders', args.orderId);
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) return { error: 'Purchase order not found' };

      await updateDoc(orderRef, {
        status: args.status,
        updatedAt: serverTimestamp(),
        updatedBy: context.currentUser?.uid
      });
      return { success: true, orderId: args.orderId, newStatus: args.status };
    }
  });
}
